export enum ELoginType {
  ONE_CLICK = 1, // 运营商一键登录
  PHONE_VERIFICATION_CODE = 2, // 手机验证码登录
  FIREBASE = 3, // firebase 登录
  INVITATION_CODE = 4, // 邀请码
  ANONYMOUS = 5, // 匿名登录
}

export enum OSType {
  IOS = 1,
  ANDROID = 2,
  WEB = 3,
}

export interface ILoginReq {
  login_type: ELoginType;
  os?: OSType;
  firebase_token?: string;
  referral_code?: string;
  device_info?: IRegisterDeviceReq; // 设备注册信息
  phone_number?: string;
  verification_code?: string;
  not_logout_other_device?: boolean;
}

export interface ILoginResp {
  auth_token: string;
  is_new?: boolean;
  is_anonymous: boolean;
  referral_code?: string;
  refresh_token?: string;
  user_id: number;
  email?: string;
  device_id?: number;
  unique_id?: number;
  im_account_info?: {
    account?: string;
    password?: string;
  };
}

export interface ILogoutReq {
  refresh_token: string;
}

export interface INpcPoolItem {
  id: number;
  name: string;
  img_url: string;
  small_img_url: string;
  prologue: string;
  audio_url: string;
  out_audio_url: string;
}

export interface IMediaConfig {
  title: string;
  schema?: string;
  url?: string;
  share_url?: string;
  icon: string;
  light_icon?: string;
}

interface ICareerJobContent {
  title: string;
  contents: string[] | string;
}

export interface ICareerJobItem {
  title: string;
  subTitle?: string;
  contents: ICareerJobContent[];
}

export interface IConversationNpcItem {
  id: number;
  name: string;
  avatarImgs: string[];
  prologue: string;
}

export interface IConversationNpcItemV2 {
  id: number;
  prompts: string[];
}

export interface ITaskNpcItem {
  id: number;
  name: string;
  desc: string;
  backgroundImg: string;
}

export interface IAppSettings {
  app_online: boolean;
  ios_download_url: string;
  android_download_url: string;
  download_qrcode: string;
  medias: string[];
  share_medias: string[];
  npc_share_url: string;
  media_configs?: IMediaConfig[];
  minimum_available_age: number;
  auto_play_audio?: boolean;
  conversation_npc_list?: IConversationNpcItem[];
  conversation_npc_list_v2?: IConversationNpcItemV2[];
  npc_pool_list?: INpcPoolItem[];
  task_npc_list?: ITaskNpcItem[];
  web_schemas?: string;
  maxAnonymousChatCount?: number; // 聊天限制轮数
  maxAnonymousChatCountExp?: number; // 聊天限制轮数（实验组）
  maxAnonymousChatCountExpPercent?: number; // 实验组百分比
  chat_close_door_ad_whitelist?: number[];
  chat_close_door_ad_threshold?: number;
  chat_close_door_ad_append_threshold?: number;
  web_ugc_disable_draw_setting?: boolean;
  web_ugc_create_book_url?: string;
  web_ugc_enable_agent_create_npc?: boolean;
  enable_agent_create_npc_user_ids?: number[];
  // NPC Studio 相关灰度配置 (web_settings.npc_studio_settings 嵌套, 后端 web_ugc 通用 Apollo 入口)
  // - web_ugc_enable_npc_lab: NPC Lab 内测路由总开关
  // - enable_npc_lab_user_ids: NPC Lab user_id 白名单
  // - web_ugc_enable_quick_create: QuickCreate (/agent/quick-create) 网页总开关
  // - quick_create_whitelist_device_ids: QuickCreate device_id 白名单
  // - quick_create_field_labels: QuickCreate 字段展示名映射 (key=npc 字段名, value=展示标签)
  //   例 { "hidden_description": "简介", "description": "设定", "greeting": "开场白", "voice": "音色" }
  //   前端 fallback hardcode 默认值, 没配字段保留 hardcode.
  // - quick_create_visible_fields: QuickCreate 字段可见性白名单 (key=npc 字段名, 未列入的字段隐藏)
  //   例 ["hidden_description", "greeting", "voice"] 只显示这 3 个 (隐藏 description 设定字段)
  //   空数组 / undefined / 字段不存在于此 list = **全部显示** (兼容现有行为). 想要隐藏字段才配此项.
  //   可配 key: hidden_description / description / greeting / voice (display_name / gender / avatar 永远显示)
  npc_studio_settings?: {
    web_ugc_enable_npc_lab?: boolean;
    enable_npc_lab_user_ids?: number[];
    web_ugc_enable_quick_create?: boolean;
    quick_create_whitelist_device_ids?: number[];
    quick_create_field_labels?: Record<string, string>;
    quick_create_visible_fields?: string[];
  };
  disable_edit_chatbox?: boolean; // 隐藏编辑-对话调试框 for安全
  disable_login_page?: boolean; // 下掉登录页 for安全
}

export interface IRegisterDeviceReq {
  os: OSType;
  idfa?: string;
  idfv?: string;
  android_id?: string;
  oaid?: string;
  uuid?: string; // web 使用
}

export interface IIMAccountInfo {
  account: string;
  password: string;
}

export interface ICheckExistsInFirebaseReq {
  provider_id: string;
  provider_uid: string;
}

export interface ICheckExistsInFirebaseResp {
  exists?: boolean;
}

export interface IRefreshAuthTokenReq {
  refresh_token: string;
}

export interface IRefreshAuthTokenResp {
  auth_token: string;
  refresh_token: string;
}

// blog 数据类型
export interface Feature {
  title: string;
  text?: string;
}

export interface SubSection {
  sectionTitle?: string;
  introduction?: string[] | string;
  image?: string;
  features?: Feature[];
  text?: string;
}

export interface ArticleData {
  id: number;
  title: string;
  date: string;
  banner: string;
  introduction: string[];
  subSections: SubSection[];
  relatedImage: string;
}

export const HTTP_STATUS_CODE = {
  SUCCESS: [200, 201, 204],
  NEED_REFRESH_TOKEN: 1101010121,
  REFRESH_TOKEN_FAIL: 1101010111,
  AUTH_TOKEN_MISSED: 1118010010,
};

export enum VerifyType {
  auto = 0,
  picture = 1, // 图像验证码
}

export interface ISendVerificationCodeReq {
  target?: string; // 手机号
  ticket?: string; // 客户端获取的票据
  randstr?: string; // 客户端获取的随机字符串
  verify_type?: VerifyType;
}

export interface ISendVerificationCodeResp {}
